import { clsx } from 'clsx';
import type { JSX, ElementType, PropsWithChildren } from 'react';

import type { AccessibilityProps } from '../../internal/utils/getAccessibilityProps';
import type { ReeferBaseProps } from '../../utils/baseProps.types';

import styles from './hide.module.css';

interface InnerHideProps
  extends
    ReeferBaseProps,
    Pick<AccessibilityProps, 'aria-atomic' | 'aria-live'> {
  /** Render the `Hide` component as this HTML element. Defaults to `div`. */
  as?: keyof JSX.IntrinsicElements;

  /** Hides the children of this component from the screen readers */
  hideFromScreenReader?: boolean;

  /** Hides the children of this component */
  isHidden?: boolean;
}

export type HideProps = PropsWithChildren<InnerHideProps>;

/**
 * Hide is a simple helper component that can be used to programmatically hide content.
 * This can be useful for accessibility purposes, as well as many other things.
 */
export function Hide({
  as = 'div',
  children,
  className,
  'data-testid': testId,
  hideFromScreenReader = false,
  id,
  isHidden = false,
  style,
  ...ariaProps
}: HideProps) {
  const RenderAs: ElementType = as;
  return (
    <RenderAs
      {...ariaProps}
      className={clsx(className, {
        [styles['isHidden']]: isHidden,
        [styles['hideFromScreenReader']]: isHidden && hideFromScreenReader,
      })}
      data-testid={testId}
      id={id}
      style={style}
    >
      {children}
    </RenderAs>
  );
}
